import { Fragment, useEffect, useMemo, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { officeSensorTrafficApi, type OfficeSensorTrafficPacket } from '../services/officeSensors';

const DATETIME_FORMATTER = new Intl.DateTimeFormat('sv-SE', {
  dateStyle: 'short',
  timeStyle: 'short',
});

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : DATETIME_FORMATTER.format(date);
}

function formatNumber(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return value.toFixed(digits).replace('.', ',');
}

function formatBool(value: boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return value ? 'Ja' : 'Nej';
}

export function OfficeSensorTrafficPage() {
  const { error: toastError } = useToast();
  const [items, setItems] = useState<OfficeSensorTrafficPacket[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        const response = await officeSensorTrafficApi.list(100);
        if (!alive) {
          return;
        }
        setItems(response.items);
      } catch (error) {
        if (!alive) {
          return;
        }
        toastError(`Kunde inte läsa sensortrafik: ${error instanceof Error ? error.message : 'Okänt fel'}`);
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      alive = false;
    };
  }, [toastError]);

  const totalBeehives = useMemo(() => items.reduce((sum, packet) => sum + packet.beehiveCount, 0), [items]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[30px]">Sensortrafik</h1>
          <p className="text-sm text-slate-600">Visar inkommande paket från ApisAI-Link samt alla beehive-rader i varje paket.</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
          {items.length} paket, {totalBeehives} beehive-rader
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-600 shadow-sm">Läser sensortrafik…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-600 shadow-sm">Inga sensordata har kommit in ännu.</div>
      ) : (
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <div className="max-h-[calc(100vh-240px)] overflow-auto">
            <table className="min-w-full border-separate border-spacing-0">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                  <th className="border-b border-slate-200 px-4 py-3">Mottaget</th>
                  <th className="border-b border-slate-200 px-4 py-3">Gateway</th>
                  <th className="border-b border-slate-200 px-4 py-3">Gateway MAC</th>
                  <th className="border-b border-slate-200 px-4 py-3">Ström</th>
                  <th className="border-b border-slate-200 px-4 py-3">Bin</th>
                  <th className="border-b border-slate-200 px-4 py-3">Detaljer</th>
                </tr>
              </thead>
              <tbody>
                {items.map((packet) => (
                  <Fragment key={packet.id}>
                    <tr className="align-top">
                      <td className="border-b border-slate-100 px-4 py-4 text-sm text-slate-900">{formatDateTime(packet.receivedAt)}</td>
                      <td className="border-b border-slate-100 px-4 py-4">
                        <div className="text-sm font-semibold text-slate-950">{packet.gatewayName ?? '—'}</div>
                        <div className="text-xs text-slate-500">Gatewaytid: {formatDateTime(packet.gatewayTime)}</div>
                      </td>
                      <td className="border-b border-slate-100 px-4 py-4 text-sm text-slate-700">{packet.gatewayMac}</td>
                      <td className="border-b border-slate-100 px-4 py-4 text-sm text-slate-700">
                        <div>Batteri: {formatNumber(packet.powerBatteryV, 2)} V</div>
                        <div>Procent: {formatNumber(packet.powerBatteryPct, 0)} %</div>
                        <div>Sol: {formatNumber(packet.powerSolarV, 2)} V</div>
                      </td>
                      <td className="border-b border-slate-100 px-4 py-4 text-sm text-slate-900">{packet.beehiveCount}</td>
                      <td className="border-b border-slate-100 px-4 py-4 text-sm text-slate-700">
                        <div>Boot: {packet.gatewayBootCount ?? '—'}</div>
                        <div>Uptime: {packet.gatewayUptimeS !== null ? `${packet.gatewayUptimeS} s` : '—'}</div>
                        <div>Tid giltig: {formatBool(packet.gatewayTimeValid)}</div>
                        <div>Källa: {packet.gatewayTimeSource ?? '—'}</div>
                      </td>
                    </tr>
                    <tr>
                      <td className="border-b border-slate-100 px-4 pb-4 pt-0" colSpan={6}>
                        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-sm font-semibold text-slate-900">Beehives</div>
                          {packet.beehives.length === 0 ? (
                            <div className="text-sm text-slate-600">Inga beehive-rader i detta paket.</div>
                          ) : (
                            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                              <table className="min-w-full border-separate border-spacing-0">
                                <thead className="bg-white">
                                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                                    <th className="border-b border-slate-200 px-3 py-2">MAC</th>
                                    <th className="border-b border-slate-200 px-3 py-2">Namn</th>
                                    <th className="border-b border-slate-200 px-3 py-2">Typ</th>
                                    <th className="border-b border-slate-200 px-3 py-2">RSSI</th>
                                    <th className="border-b border-slate-200 px-3 py-2">OK</th>
                                    <th className="border-b border-slate-200 px-3 py-2">Vikt g</th>
                                    <th className="border-b border-slate-200 px-3 py-2">Temp °C</th>
                                    <th className="border-b border-slate-200 px-3 py-2">Ljud rå</th>
                                    <th className="border-b border-slate-200 px-3 py-2">Batteri V</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {packet.beehives.map((beehive) => (
                                    <tr key={beehive.id} className="text-sm text-slate-700">
                                      <td className="border-b border-slate-100 px-3 py-2 font-mono text-xs text-slate-900">{beehive.mac}</td>
                                      <td className="border-b border-slate-100 px-3 py-2">{beehive.name ?? '—'}</td>
                                      <td className="border-b border-slate-100 px-3 py-2">{beehive.sensorType ?? '—'}</td>
                                      <td className="border-b border-slate-100 px-3 py-2">{beehive.rssi ?? '—'}</td>
                                      <td className="border-b border-slate-100 px-3 py-2">{beehive.ok ? 'Ja' : 'Nej'}</td>
                                      <td className="border-b border-slate-100 px-3 py-2">{formatNumber(beehive.weightG, 3)}</td>
                                      <td className="border-b border-slate-100 px-3 py-2">{formatNumber(beehive.temperatureC, 2)}</td>
                                      <td className="border-b border-slate-100 px-3 py-2">{formatNumber(beehive.soundRaw, 2)}</td>
                                      <td className="border-b border-slate-100 px-3 py-2">{formatNumber(beehive.batteryV, 3)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <details className="rounded-xl border border-slate-200 bg-white p-3">
                            <summary className="cursor-pointer text-sm font-semibold text-slate-900">Rå JSON</summary>
                            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-slate-700">
                              {JSON.stringify(packet, null, 2)}
                            </pre>
                          </details>
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
